import { Participant, Tournament } from '@/src/domain/models';
import type { LeaderboardEntry } from '@/src/domain/usecases/GetBracketLeaderboardUseCase';
import type { RoundRobinStanding } from '@/src/domain/usecases/GetRoundRobinStandingsUseCase';


interface PDFTemplateProps {
  tournament: Tournament;
  participants?: Participant[];
  standings?: RoundRobinStanding[];
  leaderboard?: LeaderboardEntry[];
  champion?: Participant | null;
  showWatermark?: boolean;
}

const TOURNAMENT_TYPE_LABELS: Record<string, string> = {
  single_elimination: 'Single Elimination',
  double_elimination: 'Double Elimination',
  round_robin: 'Round Robin',
  swiss: 'Swiss System',
  triple_elimination: 'Triple Elimination',
  king_of_the_hill: 'King of the Hill',
};

export const generateTournamentPDFHTML = ({
  tournament,
  participants,
  standings,
  leaderboard,
  champion,
  showWatermark = false,
}: PDFTemplateProps): string => {
  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const typeName = TOURNAMENT_TYPE_LABELS[tournament.tournamentType] ?? tournament.tournamentType;
  const isCompleted = tournament.status === 'completed';
  const isStandingsBased =
    tournament.tournamentType === 'round_robin' || tournament.tournamentType === 'swiss';
  const participantCount =
    participants?.length ?? standings?.length ?? leaderboard?.length ?? 0;

  // ─── Helpers ──────────────────────────────────────────────────────────────

  const rowBg = (index: number) => {
    if (index === 0) return 'background: linear-gradient(90deg,#fffbeb,#fef3c7);';
    if (index === 1) return 'background:#f8fafc;';
    if (index === 2) return 'background:#fff7ed;';
    return index % 2 === 0 ? 'background:#ffffff;' : 'background:#fafafa;';
  };

  const medal = (pos: number) => {
    if (pos === 1) return '🥇';
    if (pos === 2) return '🥈';
    if (pos === 3) return '🥉';
    return `#${pos}`;
  };

  // ─── Standings table (Round Robin / Swiss) ────────────────────────────────

  const standingsHTML = isStandingsBased && standings
    ? `
    <h2 style="font-size:18px;font-weight:700;color:#1e293b;margin:0 0 14px;padding-bottom:10px;border-bottom:2px solid #e2e8f0;">
      Final Standings
    </h2>
    <table style="width:100%;border-collapse:collapse;margin-bottom:10px;">
      <thead>
        <tr style="background:#1e293b;color:white;">
          <th style="padding:11px 14px;font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:.5px;text-align:left;">Rank</th>
          <th style="padding:11px 14px;font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:.5px;text-align:left;">Participant</th>
          <th style="padding:11px 14px;font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:.5px;text-align:center;">P</th>
          <th style="padding:11px 14px;font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:.5px;text-align:center;">W</th>
          <th style="padding:11px 14px;font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:.5px;text-align:center;">D</th>
          <th style="padding:11px 14px;font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:.5px;text-align:center;">L</th>
          ${tournament.tournamentType === 'round_robin' ? '<th style="padding:11px 14px;font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:.5px;text-align:center;">GD</th>' : ''}
          <th style="padding:11px 14px;font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:.5px;text-align:center;">Pts</th>
        </tr>
      </thead>
      <tbody>
        ${standings
          .map(
            (s, i) => `
        <tr style="border-bottom:1px solid #f1f5f9;${rowBg(i)}">
          <td style="padding:13px 14px;font-size:16px;">${medal(i + 1)}</td>
          <td style="padding:13px 14px;font-size:14px;font-weight:${i < 3 ? '700' : '500'};color:#1e293b;">${s.participant.name}</td>
          <td style="padding:13px 14px;font-size:14px;text-align:center;color:#64748b;">${s.played}</td>
          <td style="padding:13px 14px;font-size:14px;text-align:center;color:#10b981;font-weight:600;">${s.won}</td>
          <td style="padding:13px 14px;font-size:14px;text-align:center;color:#64748b;">${s.drawn}</td>
          <td style="padding:13px 14px;font-size:14px;text-align:center;color:#ef4444;font-weight:600;">${s.lost}</td>
          ${
            tournament.tournamentType === 'round_robin'
              ? `<td style="padding:13px 14px;font-size:14px;text-align:center;color:${s.goalDifference >= 0 ? '#10b981' : '#ef4444'};font-weight:600;">${s.goalDifference >= 0 ? '+' : ''}${s.goalDifference}</td>`
              : ''
          }
          <td style="padding:13px 14px;font-size:16px;text-align:center;font-weight:800;color:#2563eb;">${s.points}</td>
        </tr>
        `
          )
          .join('')}
      </tbody>
    </table>
    <div style="background:#f8fafc;border-radius:6px;padding:8px 14px;font-size:11px;color:#64748b;margin-bottom:32px;">
      <strong>Legend:</strong> P = Played · W = Won · D = Drawn · L = Lost${tournament.tournamentType === 'round_robin' ? ' · GD = Goal Difference' : ''} · Pts = Points
    </div>
    `
    : '';

  // ─── Leaderboard table (Elimination) ──────────────────────────────────────

  const leaderboardHTML = !isStandingsBased && leaderboard
    ? `
    <h2 style="font-size:18px;font-weight:700;color:#1e293b;margin:0 0 14px;padding-bottom:10px;border-bottom:2px solid #e2e8f0;">
      Final Rankings
    </h2>
    <table style="width:100%;border-collapse:collapse;margin-bottom:32px;">
      <thead>
        <tr style="background:#1e293b;color:white;">
          <th style="padding:11px 14px;font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:.5px;text-align:left;">Position</th>
          <th style="padding:11px 14px;font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:.5px;text-align:left;">Participant</th>
          <th style="padding:11px 14px;font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:.5px;text-align:center;">Wins</th>
          <th style="padding:11px 14px;font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:.5px;text-align:center;">Losses</th>
          <th style="padding:11px 14px;font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:.5px;text-align:center;">Win Rate</th>
        </tr>
      </thead>
      <tbody>
        ${leaderboard
          .map(
            (e, i) => `
        <tr style="border-bottom:1px solid #f1f5f9;${rowBg(i)}">
          <td style="padding:13px 14px;font-size:16px;">${medal(e.position)}${e.status === 'champion' ? ' <span style="font-size:11px;font-weight:700;color:#92400e;background:#fef3c7;padding:2px 8px;border-radius:10px;margin-left:4px;">CHAMPION</span>' : ''}</td>
          <td style="padding:13px 14px;font-size:14px;font-weight:${e.status === 'champion' ? '700' : '500'};color:#1e293b;">${e.participant.name}</td>
          <td style="padding:13px 14px;font-size:14px;text-align:center;color:#10b981;font-weight:600;">${e.wins}</td>
          <td style="padding:13px 14px;font-size:14px;text-align:center;color:#ef4444;font-weight:600;">${e.losses}</td>
          <td style="padding:13px 14px;font-size:14px;text-align:center;color:#2563eb;font-weight:600;">${e.winRate.toFixed(0)}%</td>
        </tr>
        `
          )
          .join('')}
      </tbody>
    </table>
    `
    : '';

  // ─── Watermark ────────────────────────────────────────────────────────────

  const watermarkHTML = showWatermark
    ? `
    <div style="
      position:fixed;top:50%;left:50%;
      transform:translate(-50%,-50%) rotate(-45deg);
      font-size:68px;font-weight:900;
      color:rgba(0,0,0,0.055);
      white-space:nowrap;pointer-events:none;
      letter-spacing:10px;font-family:sans-serif;
      z-index:9999;
    ">TOURNALY FREE</div>
    `
    : '';

  // ─── Info cards ───────────────────────────────────────────────────────────

  const startDateCard = tournament.startDate
    ? `
    <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:10px;padding:14px 18px;">
      <div style="font-size:11px;font-weight:600;color:#94a3b8;text-transform:uppercase;letter-spacing:1px;margin-bottom:4px;">Start Date</div>
      <div style="font-size:17px;font-weight:700;color:#1e293b;">
        ${new Date(tournament.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
      </div>
    </div>
    `
    : '';

  const descriptionSection = tournament.description
    ? `
    <div style="background:#f0f9ff;border-left:4px solid #2563eb;border-radius:0 8px 8px 0;padding:14px 18px;margin-bottom:32px;">
      <div style="font-size:11px;font-weight:600;color:#2563eb;text-transform:uppercase;letter-spacing:1px;margin-bottom:6px;">Tournament Rules</div>
      <div style="font-size:14px;color:#374151;line-height:1.6;">${tournament.description}</div>
    </div>
    `
    : '';

  // ─── Footer ───────────────────────────────────────────────────────────────

  const footerHTML = showWatermark
    ? `
    <div style="margin-top:48px;padding-top:20px;border-top:1px solid #e2e8f0;text-align:center;">
      <div style="font-size:13px;color:#64748b;">
        Generated with <strong style="color:#2563eb;">Tournaly Free</strong> — Tournament Management
      </div>
      <div style="font-size:11px;color:#94a3b8;margin-top:6px;">
        Remove watermark and unlock all features at <strong>tournaly.app</strong>
      </div>
    </div>
    `
    : `
    <div style="margin-top:48px;padding-top:20px;border-top:1px solid #e2e8f0;text-align:center;">
      <div style="font-size:13px;color:#64748b;">
        Generated by <strong style="color:#2563eb;">Tournaly Pro</strong> — Tournament Management
      </div>
      <div style="font-size:11px;color:#d1d5db;margin-top:4px;">
        © ${new Date().getFullYear()} Tournaly. All rights reserved.
      </div>
    </div>
    `;

  // ─── Full template ────────────────────────────────────────────────────────

  return `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width,initial-scale=1.0">
        <title>${tournament.name} — Tournaly</title>
        <style>
          @page { size: A4 portrait; margin: 0; }
          * { margin:0; padding:0; box-sizing:border-box; }
          html { width:210mm; }
          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
            background:#ffffff;
            color:#1f2937;
            line-height:1.5;
            width:210mm;
          }
          @media print {
            html, body { width:210mm; }
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          }
        </style>
      </head>
      <body>

        ${watermarkHTML}

        <!-- ── Header ── -->
        <div style="background:linear-gradient(135deg,#1e3a5f 0%,#2563eb 100%);color:white;padding:40px;text-align:center;">
          <div style="margin-bottom:14px;">
            <span style="font-size:13px;font-weight:700;letter-spacing:4px;text-transform:uppercase;opacity:.85;">TOURNALY</span>
          </div>
          <h1 style="font-size:36px;font-weight:800;margin-bottom:14px;line-height:1.2;">
            ${tournament.name}
          </h1>
          <div style="margin-bottom:14px;">
            <span style="display:inline-block;background:rgba(255,255,255,.18);color:white;padding:5px 14px;border-radius:20px;font-size:13px;font-weight:600;margin:0 4px;">
              ${typeName}
            </span>
            <span style="display:inline-block;background:${isCompleted ? '#10b981' : '#f59e0b'};color:white;padding:5px 14px;border-radius:20px;font-size:13px;font-weight:700;margin:0 4px;">
              ${isCompleted ? 'COMPLETED' : 'IN PROGRESS'}
            </span>
          </div>
          <div style="font-size:13px;opacity:.65;">Generated on ${currentDate}</div>
        </div>

        <!-- ── Content ── -->
        <div style="padding:40px;">

          <!-- Champion banner -->
          ${champion && isCompleted
            ? `
          <div style="background:linear-gradient(135deg,#fbbf24,#f59e0b 60%,#d97706);border-radius:16px;padding:32px;text-align:center;margin-bottom:32px;box-shadow:0 6px 20px rgba(245,158,11,.28);">
            <div style="font-size:52px;margin-bottom:8px;">🏆</div>
            <div style="font-size:12px;font-weight:700;letter-spacing:4px;color:#78350f;margin-bottom:10px;">CHAMPION</div>
            <div style="font-size:34px;font-weight:800;color:#451a03;">${champion.name}</div>
          </div>
          `
            : ''}

          <!-- Info grid -->
          <div style="display:grid;grid-template-columns:repeat(2,1fr);gap:14px;margin-bottom:32px;">
            <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:10px;padding:14px 18px;">
              <div style="font-size:11px;font-weight:600;color:#94a3b8;text-transform:uppercase;letter-spacing:1px;margin-bottom:4px;">Format</div>
              <div style="font-size:17px;font-weight:700;color:#1e293b;">${typeName}</div>
            </div>
            <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:10px;padding:14px 18px;">
              <div style="font-size:11px;font-weight:600;color:#94a3b8;text-transform:uppercase;letter-spacing:1px;margin-bottom:4px;">Status</div>
              <div style="font-size:17px;font-weight:700;color:#1e293b;">${isCompleted ? '✅ Completed' : '⏳ In Progress'}</div>
            </div>
            <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:10px;padding:14px 18px;">
              <div style="font-size:11px;font-weight:600;color:#94a3b8;text-transform:uppercase;letter-spacing:1px;margin-bottom:4px;">Participants</div>
              <div style="font-size:17px;font-weight:700;color:#1e293b;">${participantCount}</div>
            </div>
            ${startDateCard}
          </div>

          ${descriptionSection}

          <!-- Results -->
          ${standingsHTML}
          ${leaderboardHTML}

          ${footerHTML}

        </div>
      </body>
    </html>
  `;
};
